import { setFooterSettings } from '@/components/FooterDateBar';

export const HANDSFREE_KEY = 'mamv-voice-handsfree';
export const VOICE_START_EVENT = 'mamv-voice-start';
export const VOICE_SEARCH_EVENT = 'mamv-voice-search';
export const WAKE_PHRASES = ['hey mamv', 'hallo mamv', 'ok mamv', 'mamv'];

// Die gerade angezeigten Suchbedingungen. Ohne sie hat eine Folgeselektion
// („davon nur die Klavierschüler“) keinen Bezugspunkt.
let voiceSearchCriteria = {};

export function setVoiceSearchCriteria(criteria) {
  voiceSearchCriteria = criteria && typeof criteria === 'object' ? { ...criteria } : {};
}

export function getVoiceSearchCriteria() {
  return { ...voiceSearchCriteria };
}

export function requestVoiceStart() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(VOICE_START_EVENT));
}

export function isHandsFreeEnabled() {
  return localStorage.getItem(HANDSFREE_KEY) === 'on';
}

export function setHandsFreeEnabled(on) {
  localStorage.setItem(HANDSFREE_KEY, on ? 'on' : 'off');
}

export function stripWakePhrase(text) {
  const raw = String(text || '').trim();
  const lower = raw.toLowerCase();
  const sorted = [...WAKE_PHRASES].sort((a, b) => b.length - a.length);
  for (const wake of sorted) {
    if (lower === wake) return { wakeOnly: true, command: '' };
    if (lower.startsWith(`${wake} `) || lower.startsWith(`${wake},`)) {
      return { wakeOnly: false, command: raw.slice(wake.length).replace(/^[\s,]+/, '') };
    }
  }
  return { wakeOnly: false, command: raw, needsWake: true };
}

export function pickGermanVoice(voices) {
  const list = Array.from(voices || []).filter((v) => (
    String(v.lang || '').toLowerCase().startsWith('de')
  ));
  const score = (v) => {
    const n = `${v.name || ''} ${v.voiceURI || ''}`.toLowerCase();
    let s = 0;
    if (/premium|enhanced|neural|natural|online/.test(n)) s += 6;
    if (/google/.test(n)) s += 3;
    if (/anna|hedda|katja|conrad|martin|petra|helena/.test(n)) s += 2;
    if (/compact|novelty|eloquence|pipe|flo|eddy|grandma|grandpa/.test(n)) s -= 10;
    return s;
  };
  return list.sort((a, b) => score(b) - score(a))[0] || null;
}

function speakBrowser(text, onEnd) {
  if (typeof window === 'undefined' || !window.speechSynthesis || !text) {
    if (onEnd) onEnd();
    return;
  }
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = 'de-DE';
  utter.rate = 1.08;
  utter.pitch = 1.04;
  const voices = (typeof window.speechSynthesis.getVoices === 'function'
    ? window.speechSynthesis.getVoices()
    : []) || [];
  const de = pickGermanVoice(voices);
  if (de) utter.voice = de;
  let finished = false;
  const done = () => {
    if (finished) return;
    finished = true;
    if (onEnd) onEnd();
  };
  utter.onend = done;
  utter.onerror = done;
  if (window.speechSynthesis.paused && typeof window.speechSynthesis.resume === 'function') {
    window.speechSynthesis.resume();
  }
  window.setTimeout(() => {
    window.speechSynthesis.speak(utter);
    if (!window.speechSynthesis.speaking) {
      window.setTimeout(done, 0);
    }
  }, 40);
}

/**
 * Liest vor: zuerst natürliche OpenAI-Stimme, sonst die beste Browser-Stimme.
 */
export function speakText(text, { onEnd, speakAudio } = {}) {
  if (!text) {
    if (onEnd) onEnd();
    return;
  }
  if (speakAudio) {
    Promise.resolve(speakAudio(text))
      .then(() => { if (onEnd) onEnd(); })
      .catch(() => speakBrowser(text, onEnd));
    return;
  }
  speakBrowser(text, onEnd);
}

/**
 * Führt den vom Backend geplanten Befehl aus. Kein Klick nötig.
 */
export function executeVoicePlan(plan, {
  navigate,
  requestAction,
  onNew,
  newMenu,
  setHandsFree,
  applyFooter,
} = {}) {
  if (!plan) return { handled: false };
  const intent = plan.intent;
  if (
    (intent === 'navigate' || intent === 'open' || intent === 'search')
    && plan.footer
    && typeof plan.footer === 'object'
  ) {
    if (applyFooter) applyFooter(plan.footer);
    else setFooterSettings(plan.footer);
  }

  if (intent === 'search') {
    const criteria = plan.criteria && typeof plan.criteria === 'object' ? plan.criteria : {};
    setVoiceSearchCriteria(criteria);
    if (navigate && plan.path) navigate(plan.path);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(VOICE_SEARCH_EVENT, {
        detail: { criteria, mode: plan.searchMode === 'refine' ? 'refine' : 'new' },
      }));
    }
    return { handled: true };
  }

  if (intent === 'handsfree_on') {
    setHandsFreeEnabled(true);
    if (setHandsFree) setHandsFree(true);
    return { handled: true };
  }
  if (intent === 'handsfree_off') {
    setHandsFreeEnabled(false);
    if (setHandsFree) setHandsFree(false);
    return { handled: true };
  }
  if (intent === 'stop' || intent === 'help' || intent === 'clarify' || intent === 'speak') {
    return { handled: true };
  }
  if (intent === 'back') {
    if (navigate) navigate(-1);
    return { handled: true };
  }
  if (intent === 'action' && plan.action && requestAction) {
    requestAction(plan.action);
    return { handled: true };
  }
  if (intent === 'create') {
    if (plan.create === 'enrollment' && Array.isArray(newMenu)) {
      const item = newMenu.find((m) => /belegung/i.test(m.label || m.id || ''));
      if (item?.onClick) {
        item.onClick();
        return { handled: true };
      }
    }
    if (plan.create === 'student') {
      if (navigate) navigate('/students/new');
      return { handled: true };
    }
    if (onNew) {
      onNew();
      return { handled: true };
    }
    if (plan.create === 'enrollment' && navigate) {
      navigate('/unterrichtsverwaltung');
      return { handled: true };
    }
    return { handled: false };
  }
  if ((intent === 'navigate' || intent === 'open') && plan.path && navigate) {
    navigate(plan.path);
    return { handled: true };
  }
  return { handled: false };
}

export function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}
